import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export type MappingStatus = 'pending' | 'accepted' | 'rejected'
export type SubmissionStatus = 'draft' | 'submitting' | 'submitted'

export interface FieldMappingState {
    mappingId: string
    csvColumn: string
    aiSuggestion: string | null
    aiConfidence?: number
    aiReasoning?: string
    status: MappingStatus
    userChoice?: string // The field they manually selected
}

export interface CustomField {
    id: string
    fieldName: string
    displayLabel: string
    fieldType: string
    isEncrypted: boolean
    category?: string
    description?: string
}

export interface JobMappingState {
    mappings: { [mappingId: string]: FieldMappingState }
    submissionStatus: SubmissionStatus
    customFields: CustomField[]
    lastUpdated: string
}

export interface FieldMappingSliceState {
    jobs: { [jobId: string]: JobMappingState }
}

const initialState: FieldMappingSliceState = {
    jobs: {},
}

export const fieldMappingSlice = createSlice({
    name: 'fieldMapping',
    initialState,
    reducers: {
        initializeJob: (
            state,
            action: PayloadAction<{
                jobId: string
                mappings: Array<{
                    mappingId: string
                    csvColumn: string
                    aiSuggestion: string | null
                    aiConfidence?: number
                    aiReasoning?: string
                }>
            }>,
        ) => {
            const { jobId, mappings } = action.payload
            state.jobs[jobId] = {
                mappings: {},
                submissionStatus: 'draft',
                customFields: [],
                lastUpdated: new Date().toISOString(),
            }

            mappings.forEach((mapping) => {
                state.jobs[jobId].mappings[mapping.mappingId] = {
                    mappingId: mapping.mappingId,
                    csvColumn: mapping.csvColumn,
                    aiSuggestion: mapping.aiSuggestion,
                    aiConfidence: mapping.aiConfidence,
                    aiReasoning: mapping.aiReasoning,
                    status: 'pending',
                }
            })
        },

        acceptAISuggestion: (state, action: PayloadAction<{ jobId: string; mappingId: string }>) => {
            const { jobId, mappingId } = action.payload
            if (state.jobs[jobId]?.mappings[mappingId]) {
                state.jobs[jobId].mappings[mappingId].status = 'accepted'
                // Clear any manual user choice when accepting AI suggestion
                delete state.jobs[jobId].mappings[mappingId].userChoice
                state.jobs[jobId].lastUpdated = new Date().toISOString()
            }
        },

        rejectAISuggestion: (state, action: PayloadAction<{ jobId: string; mappingId: string }>) => {
            const { jobId, mappingId } = action.payload
            if (state.jobs[jobId]?.mappings[mappingId]) {
                state.jobs[jobId].mappings[mappingId].status = 'rejected'
                // Clear any manual user choice when rejecting AI suggestion
                delete state.jobs[jobId].mappings[mappingId].userChoice
                state.jobs[jobId].lastUpdated = new Date().toISOString()
            }
        },

        setUserChoice: (state, action: PayloadAction<{ jobId: string; mappingId: string; fieldValue: string }>) => {
            const { jobId, mappingId, fieldValue } = action.payload
            if (state.jobs[jobId]?.mappings[mappingId]) {
                state.jobs[jobId].mappings[mappingId].userChoice = fieldValue
                // When user makes a manual choice, set status appropriately
                const mapping = state.jobs[jobId].mappings[mappingId]
                if (fieldValue === 'unmapped') {
                    // For unmapped, reject the AI suggestion if it exists
                    mapping.status = mapping.aiSuggestion ? 'rejected' : 'pending'
                } else if (fieldValue === mapping.aiSuggestion) {
                    // If matches AI suggestion, accept it
                    mapping.status = 'accepted'
                } else {
                    // For other manual choices, mark as pending
                    mapping.status = 'pending'
                }
                state.jobs[jobId].lastUpdated = new Date().toISOString()
            }
        },

        clearUserChoice: (state, action: PayloadAction<{ jobId: string; mappingId: string }>) => {
            const { jobId, mappingId } = action.payload
            if (state.jobs[jobId]?.mappings[mappingId]) {
                delete state.jobs[jobId].mappings[mappingId].userChoice
                state.jobs[jobId].mappings[mappingId].status = 'pending'
                state.jobs[jobId].lastUpdated = new Date().toISOString()
            }
        },

        setSubmissionStatus: (state, action: PayloadAction<{ jobId: string; status: SubmissionStatus }>) => {
            const { jobId, status } = action.payload
            if (state.jobs[jobId]) {
                state.jobs[jobId].submissionStatus = status
                state.jobs[jobId].lastUpdated = new Date().toISOString()
            }
        },

        clearJob: (state, action: PayloadAction<{ jobId: string }>) => {
            const { jobId } = action.payload
            delete state.jobs[jobId]
        },

        addCustomField: (state, action: PayloadAction<{ jobId: string; customField: CustomField }>) => {
            const { jobId, customField } = action.payload

            // Ensure job exists before adding custom field
            if (!state.jobs[jobId]) {
                state.jobs[jobId] = {
                    mappings: {},
                    submissionStatus: 'draft',
                    customFields: [],
                    lastUpdated: new Date().toISOString(),
                }
            }

            // Ensure customFields array exists (for backwards compatibility)
            if (!state.jobs[jobId].customFields) {
                state.jobs[jobId].customFields = []
            }

            state.jobs[jobId].customFields.push(customField)
            state.jobs[jobId].lastUpdated = new Date().toISOString()
        },

        removeCustomField: (state, action: PayloadAction<{ jobId: string; fieldId: string }>) => {
            const { jobId, fieldId } = action.payload

            if (state.jobs[jobId]?.customFields) {
                state.jobs[jobId].customFields = state.jobs[jobId].customFields.filter((field) => field.id !== fieldId)
                state.jobs[jobId].lastUpdated = new Date().toISOString()
            }
        },
    },
})

export const {
    initializeJob,
    acceptAISuggestion,
    rejectAISuggestion,
    setUserChoice,
    clearUserChoice,
    setSubmissionStatus,
    clearJob,
    addCustomField,
    removeCustomField,
} = fieldMappingSlice.actions

export const fieldMappingReducer = fieldMappingSlice.reducer

// Selectors
export const selectJobMappings = (state: { fieldMapping: FieldMappingSliceState }, jobId: string) =>
    state.fieldMapping.jobs[jobId]?.mappings || {}

export const selectJobSubmissionStatus = (state: { fieldMapping: FieldMappingSliceState }, jobId: string) =>
    state.fieldMapping.jobs[jobId]?.submissionStatus || 'draft'

export const selectMappingState = (state: { fieldMapping: FieldMappingSliceState }, jobId: string, mappingId: string) =>
    state.fieldMapping.jobs[jobId]?.mappings[mappingId]

export const selectCustomFields = (state: { fieldMapping: FieldMappingSliceState }, jobId: string) =>
    state.fieldMapping.jobs[jobId]?.customFields || []
